import type { DashboardsDocument, DashboardsWidget } from '@smarthome/api';
import { AlertCircle, LayoutDashboard } from 'lucide-react';

import { getWidgetDefinition, isValidWidgetType } from '../registry';
import type { EditorWidget } from '../types';

import {
  DashboardTimeRangeProvider,
  DEFAULT_DASHBOARD_TIME_RANGE,
  type DashboardTimeRange,
} from './DashboardTimeRangeContext';
import { getGridPlacement, getMinHeight, ROW_HEIGHT_PX, sortWidgetsByPosition } from './layout';
import { WidgetFrame } from './WidgetFrame';

import { cn } from '@/components/ui';

export interface DashboardViewerProps {
  document: DashboardsDocument | undefined;
  timeRange?: DashboardTimeRange;
  className?: string;
}

/**
 * Dashboard viewer component.
 * Renders widgets in a 12-column grid on desktop, stacks to single column on mobile.
 */
export function DashboardViewer({ document, timeRange, className }: DashboardViewerProps) {
  const effectiveTimeRange = timeRange ?? DEFAULT_DASHBOARD_TIME_RANGE;
  const widgets = document?.widgets ?? [];

  if (widgets.length === 0) {
    return <EmptyDashboard />;
  }

  const sortedWidgets = sortWidgetsByPosition(widgets);

  return (
    <DashboardTimeRangeProvider timeRange={effectiveTimeRange}>
      <div className={cn('w-full', className)}>
        {/* Desktop: 12-column grid on lg+, hidden on mobile */}
        <div
          className="hidden gap-4 lg:grid"
          style={{
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
            gridAutoRows: `${ROW_HEIGHT_PX}px`,
          }}
        >
          {sortedWidgets.map((widget) => (
            <div key={widget.id} style={getGridPlacement(widget)}>
              <WidgetFrame widget={widget}>
                <WidgetBody widget={widget} />
              </WidgetFrame>
            </div>
          ))}
        </div>

        {/* Mobile/Tablet: Stacked single column */}
        <div className="flex flex-col gap-4 lg:hidden">
          {sortedWidgets.map((widget) => (
            <div key={widget.id} style={{ minHeight: getMinHeight(widget) }}>
              <WidgetFrame widget={widget} className="h-full">
                <WidgetBody widget={widget} />
              </WidgetFrame>
            </div>
          ))}
        </div>
      </div>
    </DashboardTimeRangeProvider>
  );
}

/**
 * Empty state for dashboards with no widgets
 */
function EmptyDashboard() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-8 dark:border-white/[0.06] dark:bg-[#18181b]">
      <div className="flex flex-col items-center justify-center py-8">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-800">
          <LayoutDashboard className="h-8 w-8 text-gray-400 dark:text-zinc-500" />
        </div>
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">No Widgets</h2>
        <p className="mt-2 max-w-sm text-center text-sm text-gray-500 dark:text-zinc-500">
          This dashboard has no widgets yet. Add widgets using the dashboard editor.
        </p>
      </div>
    </div>
  );
}

/**
 * Widget body renderer - renders content based on widget type
 * Uses the widget registry for registered types, falls back to legacy rendering
 */
function WidgetBody({ widget }: { widget: DashboardsWidget }) {
  const widgetType = widget.type ?? 'unknown';

  // Check if this is a registered widget type
  if (isValidWidgetType(widgetType)) {
    const def = getWidgetDefinition(widgetType);
    if (def) {
      const Renderer = def.Renderer;
      const editorWidget: EditorWidget = {
        id: widget.id ?? '',
        type: widgetType,
        title: widget.title ?? def.displayName,
        x: widget.x ?? 0,
        y: widget.y ?? 0,
        w: widget.w ?? def.defaultSize.w,
        h: widget.h ?? def.defaultSize.h,
        config: widget.config ?? def.defaultConfig,
      };
      return <Renderer widget={editorWidget} config={editorWidget.config} />;
    }
  }

  // Legacy widget types
  switch (widgetType) {
    case 'clock':
      return <ClockWidgetBody />;
    case 'weather':
      return <PlaceholderBody type="weather" label="Weather" />;
    case 'camera':
      return <PlaceholderBody type="camera" label="Camera Feed" />;
    case 'text':
      return <TextWidgetBody widget={widget} />;
    default:
      return <UnsupportedWidget type={widgetType} />;
  }
}

/**
 * Clock widget - shows current time
 */
function ClockWidgetBody() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <div className="text-3xl font-light tabular-nums text-gray-900 dark:text-white">
          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="mt-1 text-sm text-gray-500 dark:text-zinc-500">
          {new Date().toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
        </div>
      </div>
    </div>
  );
}

/**
 * Text widget - displays static text content
 */
function TextWidgetBody({ widget }: { widget: DashboardsWidget }) {
  const content = (widget.config as { text?: string })?.text ?? '';
  return (
    <div className="flex h-full items-center">
      <p className="text-sm text-gray-600 dark:text-zinc-400">{content || 'No content'}</p>
    </div>
  );
}

/**
 * Placeholder for widget types not yet implemented
 */
function PlaceholderBody({ type, label }: { type: string; label: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <div className="mb-2 h-8 w-8 rounded-lg bg-gray-100 dark:bg-zinc-800" />
      <p className="text-xs font-medium text-gray-500 dark:text-zinc-500">{label}</p>
      <p className="mt-0.5 text-[10px] text-gray-400 dark:text-zinc-600">{type}</p>
    </div>
  );
}

/**
 * Unsupported widget type fallback
 */
function UnsupportedWidget({ type }: { type: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center">
      <AlertCircle className="mb-2 h-6 w-6 text-gray-400 dark:text-zinc-600" />
      <p className="text-xs text-gray-500 dark:text-zinc-500">Unsupported widget</p>
      <p className="mt-0.5 text-[10px] text-gray-400 dark:text-zinc-600">{type}</p>
    </div>
  );
}
